import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserDashboardSettings.translations.json';
import React, {
  BaseSyntheticEvent,
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import { Flex } from 'antd';
import styled from 'styled-components';
import { colorBgContainer, colorGray } from '@atlas-frontend/design-tokens';
import { useMutationErrors } from '../../../common/queryClient';
import { keepPreviousData } from '@tanstack/react-query';
import {
  ErrorAlertWithDrawer,
  InfoIcon,
  InfoIconUrl,
  Padding,
  Spinner,
  Stack,
  useNavigationBlockers,
} from '../../../common/components';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutateUserDashboardSettingsQuery } from './query/useMutateUserDashboardSettingsQuery';
import { useGetUserDashboardSettingsQuery } from './query/useGetUserDashboardSettingsQuery';
import {
  Form,
  FormCheckbox,
  FormField,
  useFormReset,
} from '../../../common/form';
import { UserDashboardSettingsSchema } from './schema/UserDashboardSettingsSchema';
import { UserDashboardSettingsDto } from './schema/types';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useFormSubmitHandler } from '../../../common/form/AdditionalFormContext';

const { addTranslations, t } = i18nNsFactory('user-dashboard-settings');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

const CHECKBOX_FIELDS: (keyof UserDashboardSettingsDto)[] = [
  'showInputCollection',
  'showLastCalcInput',
  'showLastCalcOutput',
  'showLastCalcReport',
  'showCurrentNextLoadCalc',
  'showBookmarks',
  'showWaitingStatuses',
  'showLastTransferSubjects',
  'showCommentSample',
  'showCommentOutput',
  'showCommentSubject',
  'showSuspendedLoadings',
];

export const UserDashboardSettings = () => {
  const {
    data: settings,
    refetch,
    isLoading,
  } = useGetUserDashboardSettingsQuery({
    placeholderData: keepPreviousData,
  });

  return (
    <Flex gap="small" vertical style={{ flex: 0.5 }}>
      <UserDashboardSettingsForm
        title={t('title.default')}
        infoIconUrl={InfoIconUrl.USER_DASHBOARD_SETTINGS}
        settings={settings}
        refetch={refetch}
        isLoading={isLoading}
      >
        <UserDashboardSettingsFormDefaultInner />
      </UserDashboardSettingsForm>
      <UserDashboardSettingsForm
        title={t('title.calendar')}
        infoIconUrl={InfoIconUrl.USER_DASHBOARD_CALENDAR_SETTINGS}
        settings={settings}
        refetch={refetch}
        isLoading={isLoading}
      >
        <UserDashboardSettingsFormCalendarInner />
      </UserDashboardSettingsForm>
    </Flex>
  );
};

interface UserDashboardSettingsFormProps extends PropsWithChildren {
  title: string;
  infoIconUrl: InfoIconUrl;
  isLoading: boolean;
  refetch: () => void;
  settings?: UserDashboardSettingsDto;
}

export const UserDashboardSettingsForm: FC<UserDashboardSettingsFormProps> = (
  props,
) => {
  const { title, infoIconUrl, isLoading, refetch, settings, children } = props;

  const { clear: clearBlockers } = useNavigationBlockers();
  const { mutateAsync } = useMutateUserDashboardSettingsQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const onSuccess = useCallback(() => {
    clearBlockers();
    void refetch();
  }, [clearBlockers, refetch]);

  const handleSubmit = useCallback(
    (validFormData: UserDashboardSettingsDto) => {
      clearMutationErrors();
      return mutateAsync({
        data: validFormData,
      })
        .then(onSuccess)
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [clearMutationErrors, mutateAsync, onSuccess, setMutationErrors],
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!settings) {
    return null;
  }

  return (
    <ContentWrapper size="md">
      <StyledHeader vertical={12} horizontal>
        <Flex gap="small" align="center">
          <h4>{title}</h4>
          <InfoIcon infoIconUrl={infoIconUrl} />
        </Flex>
        {mutationErrors && (
          <ErrorAlertWithDrawer
            errorAction={t('action')}
            validationMessagesMap={mutationErrors}
          />
        )}
      </StyledHeader>
      <Padding horizontal bottom>
        <Form
          schema={UserDashboardSettingsSchema}
          defaultValues={settings}
          onSubmit={handleSubmit}
          serverErrors={mutationErrors}
        >
          {children}
        </Form>
      </Padding>
    </ContentWrapper>
  );
};

const UserDashboardSettingsFormDefaultInner = () => {
  const form = useFormContext();
  const formSubmitHandler = useFormSubmitHandler();
  const resetForm = useFormReset();

  const handleSubmit = useCallback(
    (data: FieldValues, event?: BaseSyntheticEvent) =>
      resetForm(data, formSubmitHandler(data, false, event)),
    [formSubmitHandler, resetForm],
  );

  const submit = useMemo(() => {
    return form.handleSubmit(handleSubmit, (errors, event) => {
      const values = form.getValues();
      if (import.meta.env.DEV) {
        console.error('Form validation failed', { errors, values, event });
      }
    });
  }, [form, handleSubmit]);

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent, fieldName: string) => {
      form.setValue(fieldName, event.target.checked);
      submit();
    },
    [form, submit],
  );
  return (
    <Flex vertical gap="small">
      {CHECKBOX_FIELDS.map((fieldName) => (
        <FormField key={fieldName} name={fieldName}>
          <FormCheckbox
            name={fieldName}
            onChange={(e) => handleCheckboxChange(e, fieldName)}
          >
            {t(fieldName + '.label')}
          </FormCheckbox>
        </FormField>
      ))}
    </Flex>
  );
};

const UserDashboardSettingsFormCalendarInner = () => {
  const form = useFormContext();
  const formSubmitHandler = useFormSubmitHandler();
  const resetForm = useFormReset();

  const handleSubmit = useCallback(
    (data: FieldValues, event?: BaseSyntheticEvent) =>
      resetForm(data, formSubmitHandler(data, false, event)),
    [formSubmitHandler, resetForm],
  );

  const submit = useMemo(() => {
    return form.handleSubmit(handleSubmit, (errors, event) => {
      const values = form.getValues();
      if (import.meta.env.DEV) {
        console.error('Form validation failed', { errors, values, event });
      }
    });
  }, [form, handleSubmit]);

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent, fieldName: string) => {
      form.setValue(fieldName, event.target.checked);
      submit();
    },
    [form, submit],
  );
  return (
    <Flex vertical gap="small">
      <Flex gap="small">
        {[
          'showCalendarInputDeadline',
          'showCalendarOutputDeadline',
          'showCalendarReportDeadline',
        ].map((fieldName) => (
          <FormField key={fieldName} name={fieldName}>
            <FormCheckbox
              name={fieldName}
              onChange={(e) => handleCheckboxChange(e, fieldName)}
            >
              {t(fieldName + '.label')}
            </FormCheckbox>
          </FormField>
        ))}
      </Flex>
      <Flex gap="small">
        {['showCalendarOutputPublication', 'showCalendarReportPublication'].map(
          (fieldName) => (
            <FormField key={fieldName} name={fieldName}>
              <FormCheckbox
                name={fieldName}
                onChange={(e) => handleCheckboxChange(e, fieldName)}
              >
                {t(fieldName + '.label')}
              </FormCheckbox>
            </FormField>
          ),
        )}
      </Flex>
    </Flex>
  );
};

const StyledHeader = styled(Padding)`
  border-bottom: 1px solid ${colorGray[2]};
`;

const ContentWrapper = styled(Stack)`
  background-color: ${colorBgContainer};
  border: 1px solid ${colorGray[2]};
`;
